import React, { useEffect, useState, useRef } from 'react'
import Input from 'components/input'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import Button from 'components/button'
import { ISpreadsheetContextMenuState } from 'components/spreadsheet/types'
import { INITIAL_SPREADSHEET_CONTEXT_MENU_STATE } from 'components/spreadsheet/constants/const'
import { IFormulaHelper, IFormulaDiagnostic } from 'types'
import api from 'helpers/api'
import CodeMirror from '@uiw/react-codemirror'
import { EditorView } from '@codemirror/view'
import { useResolvedTheme } from 'hooks/useTheme'
import { autocompletion, CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { tabAcceptsCompletion, containEscapeWhenCompletionOpen } from 'helpers/codemirror'

interface FormulaProps {
  columnPublicId: string
  formula: string | null
  setEditMenu: (value: ISpreadsheetContextMenuState) => void
  setHasChanges: (hasChanges: boolean) => void
}

// 'validating' can be cancelled by typing; 'saving' (the column update is in flight) cannot.
type SavePhase = 'idle' | 'validating' | 'saving'

// What a column with no formula of its own shows, and what Clear Formula puts back.
export const DEFAULT_FORMULA = '=EMPTY()'

// CodeMirror counts a line break as one unit whatever the stored line ending, and the backend
// computes diagnostic offsets over the text it is sent, so normalise before either sees it.
const normaliseLineEndings = (value: string | null): string => (value ?? '').replace(/\r\n?/g, '\n')

// Whether the editor holds nothing worth saving as a formula — either genuinely blank, or the default
// "produces nothing" formula that Clear Formula leaves behind. Saving in that state is what turns a
// formula column back into an ordinary one, so both spellings have to count.
const isEmptyFormula = (value: string): boolean => {
  const normalised = value.replace(/\s+/g, '').toUpperCase()
  return normalised === '' || normalised === DEFAULT_FORMULA
}

const Formula: React.FC<FormulaProps> = ({ columnPublicId, formula, setEditMenu, setHasChanges }) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [hoveredHelper, setHoveredHelper] = useState<IFormulaHelper | null>(null)
  const savedFormula = normaliseLineEndings(formula)
  const [formulaValue, setFormulaValue] = useState<string>(savedFormula)
  const [formulaHelper, setFormulaHelper] = useState<IFormulaHelper[]>()

  const [search, setSearch] = useState<string>('')
  const [savePhase, setSavePhase] = useState<SavePhase>('idle')
  const editorViewRef = useRef<EditorView | null>(null)
  const resolvedTheme = useResolvedTheme()

  // Reset the draft when the saved formula changes or the menu moves to another column, even one
  // whose saved formula is identical; otherwise column A's unsaved text could be saved into column B.
  useEffect(() => {
    setFormulaValue(savedFormula)
  }, [savedFormula, columnPublicId])
  const [diagnostic, setDiagnostic] = useState<IFormulaDiagnostic | null>(null)
  const saveRequestRef = useRef(0)

  useEffect(() => {
    saveRequestRef.current += 1
    setSavePhase('idle')
    setDiagnostic(null)
    return () => {
      saveRequestRef.current += 1
    }
  }, [savedFormula, columnPublicId, spreadsheetData.viewDetails.publicId])

  // Report unsaved edits up so the menu can warn before an accidental close discards the formula.
  const hasChanges = formulaValue !== savedFormula
  useEffect(() => {
    setHasChanges(hasChanges)
    // Clear the guard when the editor unmounts (menu closed, formula disabled, or saved).
    return () => setHasChanges(false)
  }, [hasChanges])

  const filteredFormulaHelper = formulaHelper
    ? formulaHelper.filter((helper) => helper.name.toLowerCase().includes(search.toLowerCase()))
    : []

  // Create autocomplete completion source from formula helpers
  const formulaCompletions = (() => {
    if (!formulaHelper) return () => null

    return (context: CompletionContext): CompletionResult | null => {
      // Get the word before cursor
      const word = context.matchBefore(/[\w$]+/)
      const charBefore = context.state.sliceDoc(context.pos - 1, context.pos)

      // Check if we're after a $ (column reference)
      const dollarMatch = context.matchBefore(/\$[\w]*\$?/)

      // Determine if we should show completions
      const shouldShow = word || charBefore === '=' || charBefore === '(' || charBefore === ','

      if (!shouldShow && !dollarMatch) return null

      const from = dollarMatch?.from ?? word?.from ?? context.pos
      const filterText = (dollarMatch?.text ?? word?.text ?? '').toLowerCase().replace(/\$/g, '')

      // Filter to columns only if typing after $
      const isColumnContext = dollarMatch !== null

      const completions: Array<{
        label: string
        detail: string
        info: () => HTMLElement
        apply: string
        type: string
        boost?: number
      }> = []

      // Add regular completions
      formulaHelper
        .filter((helper) => {
          if (isColumnContext) {
            return helper.category === 'Fields'
          }
          return true
        })
        .filter((helper) => {
          if (!filterText) return true
          return helper.name.toLowerCase().includes(filterText.toLowerCase())
        })
        .forEach((helper) => {
          const isColumn = helper.category === 'Fields'
          // For functions, insert the full syntax (without leading =)
          let applyText: string
          if (isColumn) {
            applyText = `$${helper.name}$`
          } else {
            applyText = helper.syntax.startsWith('=') ? helper.syntax.substring(1) : helper.syntax
          }
          completions.push({
            label: isColumn ? `$${helper.name}$` : helper.name,
            detail: isColumn ? 'column' : helper.category,
            info: () => {
              const div = document.createElement('div')
              div.style.padding = '8px'
              div.style.maxWidth = '400px'
              div.style.maxHeight = '300px'
              div.style.overflowY = 'auto'
              const examplesHtml = helper.examples
                .map(
                  (ex) =>
                    `<div style="font-family: monospace; background: var(--color-surface-sunken); color: var(--color-body); padding: 8px; margin-bottom: 8px; border-radius: 4px;">${ex}</div>`
                )
                .join('')
              div.innerHTML = `
                <div style="font-weight: bold; margin-bottom: 8px;">${helper.name}</div>
                <div style="margin-bottom: 8px;">${helper.description}</div>
                ${helper.syntax ? `<div style="font-family: monospace; background: var(--color-surface-sunken); color: var(--color-body); padding: 8px; margin-bottom: 8px; border-radius: 4px;">${helper.syntax}</div>` : ''}
                ${helper.examples.length > 0 ? `<div style="font-size: 12px; color: var(--color-muted); margin-bottom: 4px;">Examples:</div>${examplesHtml}` : ''}
              `
              return div
            },
            apply: applyText,
            type: isColumn ? 'variable' : 'function'
          })
        })

      if (completions.length === 0) return null

      return {
        from,
        options: completions,
        validFor: /^[\w$]*$/
      }
    }
  })()

  // Editor theme customizations. The app's own tokens, so the editor matches the
  // dialog in both themes; CodeMirror's built-in light theme left it white, with
  // a near-invisible placeholder, in dark mode. The height is capped against the panel (the root
  // is a size container) so a long formula scrolls inside the editor rather than pushing the save
  // buttons and the fields/functions list out of view, in both the docked and full-screen panel.
  const editorTheme = EditorView.theme({
    '&': {
      minHeight: '120px',
      maxHeight: '40cqh'
    },
    '&.cm-editor': {
      backgroundColor: 'var(--color-surface)',
      color: 'var(--color-body)'
    },
    '.cm-placeholder': {
      color: 'var(--color-faint)'
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: 'var(--color-body)'
    },
    '.cm-scroller': {
      minHeight: '120px',
      overflow: 'auto'
    },
    '.cm-content': {
      minHeight: '100px'
    },
    '.cm-tooltip.cm-tooltip-autocomplete': {
      background: 'var(--color-surface-raised)'
    },
    '.cm-tooltip.cm-completionInfo': {
      background: 'var(--color-surface-raised)'
    }
  })

  // CodeMirror extensions
  const extensions = [
    EditorView.lineWrapping,
    editorTheme,
    autocompletion({
      override: [formulaCompletions],
      activateOnTyping: true
    }),
    tabAcceptsCompletion,
    containEscapeWhenCompletionOpen
  ]

  useEffect(() => {
    api
      .getFormulaHelper(spreadsheetData.viewDetails.publicId, columnPublicId)
      .then((response) => {
        setFormulaHelper(response.data.filter((helper: IFormulaHelper) => helper.name !== 'ISDUPLICATE'))
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }, [columnPublicId])

  const showDiagnostic = (error: IFormulaDiagnostic) => {
    const view = editorViewRef.current
    if (view) {
      const length = view.state.doc.length
      view.dispatch({
        selection: { anchor: Math.min(error.start, length), head: Math.min(error.end, length) },
        scrollIntoView: true
      })
      view.focus()
    }
  }

  const handleFormulaChange = async () => {
    if (savePhase !== 'idle') return
    // Empty formula means the column is being cleared of its formula.
    const clearing = isEmptyFormula(formulaValue)
    const requestId = ++saveRequestRef.current
    if (!clearing) {
      setSavePhase('validating')
      try {
        const { data } = await api.validateFormula(spreadsheetData.viewDetails.publicId, columnPublicId, formulaValue)
        if (requestId !== saveRequestRef.current) return
        setDiagnostic(data.diagnostic)
        if (!data.valid) {
          if (data.diagnostic) showDiagnostic(data.diagnostic)
          setSavePhase('idle')
          return
        }
      } catch (error) {
        // The preflight is a convenience: when it is unavailable the column update below still
        // runs the authoritative checks, so a formula the backend accepts can always be saved.
        if (requestId !== saveRequestRef.current) return
      }
    }
    setSavePhase('saving')
    updateColumn(
      project.publicId,
      clearing ? 'formulaEnabled' : 'formula',
      spreadsheetData,
      setSpreadsheetData,
      columnPublicId,
      clearing ? false : formulaValue,
      () => {
        setSnackbarMessage({ status: 'success', message: 'The column was updated successfully!' })
        setSavePhase('idle')
        setEditMenu(INITIAL_SPREADSHEET_CONTEXT_MENU_STATE)
      },
      (error) => {
        setSavePhase('idle')
        displayErrorMessage(error)
      },
      {
        runScriptOnAllCells: true
      }
    )
  }

  // Get unique categories from formula helper.
  // Sort the category with name "Fields" first.
  const formulaCategories = filteredFormulaHelper
    .reduce((acc: string[], helper) => {
      if (!acc.includes(helper.category)) {
        acc.push(helper.category)
      }
      return acc
    }, [])
    .sort((a, b) => (a === 'Fields' ? -1 : b === 'Fields' ? (a > b ? 1 : a < b ? -1 : 0) : 1))

  return (
    <div className="[container-type:size] flex flex-col flex-1 min-h-0 pb-4">
      <div className="pt-4 px-2.5 shrink-0">
        <CodeMirror
          value={formulaValue}
          onChange={(value) => {
            // Typing discards a pending validation of older text, but never a save in flight.
            if (savePhase === 'validating') {
              saveRequestRef.current += 1
              setSavePhase('idle')
            }
            setDiagnostic(null)
            setFormulaValue(value)
          }}
          onCreateEditor={(view) => {
            editorViewRef.current = view
          }}
          readOnly={savePhase === 'saving'}
          extensions={extensions}
          theme={resolvedTheme}
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
            highlightActiveLineGutter: false,
            highlightActiveLine: false
          }}
          className="border border-divider"
        />
      </div>

      {diagnostic && (
        <div role="alert" className="px-2.5 py-2 text-red whitespace-normal">
          <div>
            {diagnostic.message} — line {diagnostic.line}, column {diagnostic.column}
          </div>
          <pre className="whitespace-pre-wrap break-words text-sm mt-2 max-h-[20cqh] overflow-y-auto">
            {formulaValue.slice(0, diagnostic.start)}
            <mark>{formulaValue.slice(diagnostic.start, diagnostic.end) || ' '}</mark>
            {formulaValue.slice(diagnostic.end)}
          </pre>
          <Button internalType="transparent" className="underline mt-1" onClick={() => showDiagnostic(diagnostic)}>
            Show in formula
          </Button>
        </div>
      )}
      <div className="flex items-center mb-4 gap-2.5 py-0 px-2.5">
        <Button
          className="mt-[5px] w-fit ml-auto"
          internalType="outline"
          onClick={() => setFormulaValue(DEFAULT_FORMULA)}
          disabled={isEmptyFormula(formulaValue) || savePhase !== 'idle'}
        >
          Clear Formula
        </Button>
        <Button
          className="mt-[5px] w-fit"
          internalType="accept"
          onClick={handleFormulaChange}
          isLoading={savePhase !== 'idle'}
        >
          Save Formula
        </Button>
      </div>

      {/* Fields and Functions */}

      <div className="px-2.5 flex-1 min-h-0 flex flex-col">
        <div className="flex border border-divider rounded flex-1 min-h-0">
          <div className="flex flex-col overflow-y-auto p-2.5 border-r border-r-[var(--color-divider)] flex-[0_0_clamp(220px,25%,340px)]">
            <Input
              className="mb-4"
              type="text"
              placeholder="Search"
              onChange={(event) => setSearch(event.target.value)}
            />

            {formulaCategories.map((category, categoryIndex) => {
              // For the 'Fields' category, sort alphabetically (case-insensitive)
              const helpersForCategory = filteredFormulaHelper.filter((helper) => helper.category === category)
              const sortedHelpers =
                category === 'Fields'
                  ? helpersForCategory.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                  : helpersForCategory

              return (
                <div key={categoryIndex} className="mb-8">
                  <div className="text-sm w-full text-muted text-left mb-[7px]">{category}</div>
                  {sortedHelpers.map((helper, helperIndex) => {
                    const isColumn = helper.category === 'Fields'
                    const insertText = isColumn ? `$${helper.name}$` : helper.syntax

                    return (
                      <div
                        key={helperIndex}
                        className="flex w-full items-center justify-start cursor-pointer hover:bg-surface-sunken transition-all rounded gap-[5px] p-[5px]"
                        onMouseEnter={() => setHoveredHelper(helper)}
                        onClick={() => {
                          const textToInsert = insertText.startsWith('=') ? insertText.substring(1) : insertText

                          // If we have access to the editor, replace selection or insert at cursor
                          if (editorViewRef.current) {
                            const view = editorViewRef.current
                            const { from, to } = view.state.selection.main

                            // Replace selection (or insert at cursor if no selection)
                            view.dispatch({
                              changes: { from, to, insert: textToInsert },
                              selection: { anchor: from + textToInsert.length }
                            })
                            view.focus()
                          } else {
                            // Fallback: append to end
                            if (formulaValue === DEFAULT_FORMULA) {
                              setFormulaValue(insertText)
                            } else {
                              setFormulaValue(formulaValue + textToInsert)
                            }
                          }
                        }}
                      >
                        <div className="text-sm truncate">{isColumn ? `$${helper.name}$` : helper.name}</div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>

          {!hoveredHelper && (
            <div className="flex items-center justify-center w-full p-2.5 text-muted whitespace-normal text-center">
              Hover a field or function to see what it does.
            </div>
          )}

          {hoveredHelper && (
            <div className="overflow-y-auto w-full whitespace-normal break-words p-2.5 min-w-0">
              <div className="truncate whitespace-pre-wrap text-lg font-bold mb-4">{hoveredHelper.name}</div>
              {hoveredHelper.useOnOwn && (
                <div className="mb-4 text-red">
                  <span className="font-bold">Note:</span> This formula must be used on its own (no other operators,
                  literals, or columns outside the function). It will not work reliably if combined with other functions
                  or values.
                </div>
              )}
              <div className="truncate whitespace-pre-wrap text-sm">{hoveredHelper.description}</div>
              <div className="font-bold mt-8 mb-4">Syntax</div>
              <div className="whitespace-pre-wrap text-sm bg-surface-muted rounded mb-4 p-2.5 leading-[1.7rem]">
                <pre>{hoveredHelper.syntax}</pre>
              </div>
              <div className="font-bold mt-8 mb-4">Examples</div>
              {hoveredHelper.examples.map((example, index) => (
                <pre
                  key={index}
                  className="whitespace-pre-wrap text-sm bg-surface-muted rounded mb-4 p-2.5 leading-[1.7rem]"
                >
                  {example}
                </pre>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Formula
